import React, { useState, useEffect } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import { Navbar, Nav } from 'react-bootstrap';

const BANNER_URL = 'https://iis.memphis.edu/wp-content/uploads/2025/03/cropped-UofM_horiz_rgb.jpg';

function AddUserAddress() {
  const apiPrefix = window.location.pathname.match(/^\/[^\/]+/) ? window.location.pathname.match(/^\/[^\/]+/)[0].replace(/\/$/, '') : '';
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [ip, setIp] = useState('');
  const [ipLoading, setIpLoading] = useState(true);

  useEffect(() => {
    // Fetch client IP from server (network IP)
    const fetchIp = async () => {
      setIpLoading(true);
      try {
        const res = await fetch(apiPrefix + '/api/check-ip-access', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: 'temp' })
        });
        const data = await res.json();
        setIp(data.ip || '');
      } catch {
        setIp('');
      } finally {
        setIpLoading(false);
      }
    };
    fetchIp();
  }, []);

  const [redirectCountdown, setRedirectCountdown] = useState(null);

  useEffect(() => {
    if (redirectCountdown === null) return;
    if (redirectCountdown === 0) {
      window.location.href = '/';
      return;
    }
    const timer = setTimeout(() => setRedirectCountdown(redirectCountdown - 1), 1000);
    return () => clearTimeout(timer);
  }, [redirectCountdown]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');
    try {
      const res = await fetch(apiPrefix + '/api/users/self/add-ip', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password, ip })
      });
      const data = await res.json();
      if (res.ok) {
        setMessage(`Success! Your IP (${data.ip}) has been approved. Redirecting to Overleaf in 5 seconds...`);
        setRedirectCountdown(5);
      } else {
        setMessage(data.error || 'Failed to approve IP.');
      }
    } catch (err) {
      setMessage('Network error.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="d-flex flex-column min-vh-100 bg-light">
      <header className="bg-white shadow-sm">
        <div className="container-fluid p-0">
          <img src={BANNER_URL} alt="Institute for Intelligent Systems Banner" className="img-fluid w-100" style={{ maxHeight: '90px', objectFit: 'contain', background: '#fff' }} />
        </div>
      </header>

      <div className="container py-4">
        <div className="d-flex flex-grow-1 justify-content-center align-items-center py-5">
          <div className="card shadow-lg p-4 p-md-5" style={{ maxWidth: '500px', width: '100%' }}>
            <h2 className="card-title text-center mb-3">Add Your Current IP Address</h2>
            <p className="card-subtitle text-muted text-center mb-4">Existing users can approve their current IP address for Overleaf access.</p>
            
            <form onSubmit={handleSubmit}>
              <div className="mb-3">
                <label htmlFor="ipAddress" className="form-label">Your IP Address</label>
                <input
                  type="text"
                  className="form-control"
                  id="ipAddress"
                  placeholder="Enter your IP address"
                  value={ipLoading ? 'Detecting...' : ip}
                  onChange={e => setIp(e.target.value)}
                  disabled={ipLoading}
                />
                <small className="form-text text-muted">
                  <strong>How do I find my IP?</strong> <a href="https://support.microsoft.com/en-us/windows/find-your-ip-address-in-windows-f21a9bbc-c582-55cd-35e0-73431160a1b9" target="_blank" rel="noopener noreferrer">Windows</a> | <a href="https://support.apple.com/guide/mac-help/find-your-computers-ip-address-mchlf01d5c97/mac" target="_blank" rel="noopener noreferrer">macOS</a> | <a href="https://help.gnome.org/gnome-help/net-findip.html" target="_blank" rel="noopener noreferrer">Ubuntu/GNOME</a>
                </small>
              </div>
              <div className="mb-3">
                <label htmlFor="emailAddress" className="form-label">Email Address</label>
                <input
                  type="email"
                  className="form-control"
                  id="emailAddress"
                  placeholder="Enter your email address"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  required
                />
              </div>
              <div className="mb-3">
                <label htmlFor="password" className="form-label">Password</label>
                <input
                  type="password"
                  className="form-control"
                  id="password"
                  placeholder="Enter your password"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  required
                />
              </div>
              <button
                type="submit"
                className="btn btn-success w-100 py-2"
                disabled={loading || ipLoading || !email || !password}
              >
                {loading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    Submitting...
                  </>
                ) : (
                  'Approve My IP'
                )}
              </button>
            </form>
            {message && (
              <div className={`alert mt-4 text-center ${message.startsWith('Success') ? 'alert-success' : 'alert-danger'}`} role="alert">
                {message}
              </div>
            )}
            <div className="d-flex flex-column align-items-center mt-3">
              <a href="../access-request" className="btn btn-outline-primary mb-2">Back to Access Request</a>
              <small className="text-muted text-center">
                Use this page if you are an existing user and need to re-approve your current IP address.<br />
                If you are a new user, please use the Access Request page.
              </small>
            </div>
          </div>
        </div>
      </div>
      <footer className="bg-white border-top shadow-sm mt-auto py-3">
        <div className="container text-center">
          <a href="../portal-admin" className="btn btn-outline-primary">Admin Portal</a>
        </div>
      </footer>
    </div>
  );
}

export default AddUserAddress;
